import {
  datamuseQuery,
  splitEcho,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";

/**
 * English → IPA, from the CMU Pronouncing Dictionary.
 *
 * Pronunciations arrive in ARPAbet (`F L AW1 ER0`), the CMU dictionary's own notation:
 * from the copy of the dictionary the site bundles (`pronouncing-dictionary.ts`) for
 * the words it has, and from Datamuse for the rest. Asked with `md=r` and `ipa=1`,
 * Datamuse also returns an IPA rendering (`ipa_pron:fɫˈaʊɝ`). That rendering is not
 * used as it comes, for three reasons a reader would notice:
 *
 * - it ignores stress when choosing vowels, so unstressed AH0 comes out as ʌ ("the" as
 *   ðʌ) where English dictionaries write schwa (ðə), and ER0 as ɝ instead of ɚ;
 * - it places the stress mark before the stressed vowel (fɫˈaʊɝ), where IPA puts it
 *   before the stressed syllable (ˈflaʊɚ);
 * - it writes the dark l (ɫ), a narrow detail broad dictionary transcriptions omit.
 *
 * So the converter maps ARPAbet itself: stress-aware vowels, syllables split by the
 * maximal-onset principle so each stress mark lands at the start of its syllable, no
 * mark on a one-syllable word (the dictionary convention), and Datamuse's IPA kept only
 * as a fallback for a pronunciation that cannot be parsed.
 */

/* ----------------------------------------------------------------- ARPAbet */

type Stress = 0 | 1 | 2;

export interface Phone {
  /** ARPAbet symbol without its stress digit: `AW`, `F`. */
  symbol: string;
  /** Vowels only. */
  stress: Stress | null;
}

const VOWELS: Readonly<Record<string, string>> = {
  AA: "ɑ",
  AE: "æ",
  AO: "ɔ",
  AW: "aʊ",
  AY: "aɪ",
  EH: "ɛ",
  EY: "eɪ",
  IH: "ɪ",
  IY: "i",
  OW: "oʊ",
  OY: "ɔɪ",
  UH: "ʊ",
  UW: "u",
};

const CONSONANTS: Readonly<Record<string, string>> = {
  B: "b",
  CH: "tʃ",
  D: "d",
  DH: "ð",
  F: "f",
  G: "ɡ",
  HH: "h",
  JH: "dʒ",
  K: "k",
  L: "l",
  M: "m",
  N: "n",
  NG: "ŋ",
  P: "p",
  R: "r",
  S: "s",
  SH: "ʃ",
  T: "t",
  TH: "θ",
  V: "v",
  W: "w",
  Y: "j",
  Z: "z",
  ZH: "ʒ",
};

const RE_PHONE = /^([A-Z]{1,2})([012])?$/;
const RE_SPACE = /\s+/;

/** `"F L AW1 ER0 "` → phones, or null if any symbol is not ARPAbet. */
export function parseArpabet(pron: string): Phone[] | null {
  const phones: Phone[] = [];
  for (const token of pron.trim().split(RE_SPACE)) {
    const match = RE_PHONE.exec(token);
    if (!match) {
      return null;
    }
    const [, symbol, digit] = match;
    const vowel = symbol in VOWELS || symbol === "AH" || symbol === "ER";
    if (vowel !== (digit !== undefined) || !(vowel || symbol in CONSONANTS)) {
      return null;
    }
    phones.push({ symbol, stress: vowel ? (Number(digit) as Stress) : null });
  }
  return phones.length > 0 ? phones : null;
}

/** One phone in IPA. AH and ER depend on stress: ə/ʌ and ɚ/ɝ. */
export function phoneToIpa(phone: Phone): string {
  if (phone.symbol === "AH") {
    return phone.stress === 0 ? "ə" : "ʌ";
  }
  if (phone.symbol === "ER") {
    return phone.stress === 0 ? "ɚ" : "ɝ";
  }
  return VOWELS[phone.symbol] ?? CONSONANTS[phone.symbol] ?? "";
}

/* ------------------------------------------------------------- syllables */

/**
 * Consonant clusters English allows at the start of a syllable, in ARPAbet. A cluster
 * between two vowels goes to the second syllable as far as it can (maximal onset):
 * "extreme" splits ɪk.strim, not ɪks.trim, because "str" can begin a syllable.
 */
const ONSETS: ReadonlySet<string> = new Set([
  ...Object.keys(CONSONANTS).filter((symbol) => symbol !== "NG"),
  ..."P R|P L|P Y|T R|T W|K R|K L|K W|K Y|B R|B L|B Y|D R|D W|G R|G L|G W|G Y|F R|F L|F Y|TH R|TH W|SH R|S P|S T|S K|S M|S N|S L|S W|S F|V Y|M Y|N Y|HH Y|L Y|S P R|S P L|S P Y|S T R|S K R|S K W|S K Y|S K L".split(
    "|"
  ),
]);

export interface Syllable {
  phones: Phone[];
  stress: Stress | null;
}

function isVowel(phone: Phone): boolean {
  return phone.stress !== null;
}

/** Where the onset of the next syllable begins inside a consonant cluster. */
function onsetStart(cluster: readonly Phone[]): number {
  for (let start = 0; start < cluster.length; start += 1) {
    const key = cluster
      .slice(start)
      .map((phone) => phone.symbol)
      .join(" ");
    if (ONSETS.has(key)) {
      return start;
    }
  }
  return cluster.length;
}

/** Split phones into syllables, one per vowel, by the maximal-onset principle. */
export function syllabify(phones: readonly Phone[]): Syllable[] {
  const nuclei = phones.flatMap((phone, index) =>
    isVowel(phone) ? [index] : []
  );
  if (nuclei.length === 0) {
    return [{ phones: [...phones], stress: null }];
  }
  const syllables: Syllable[] = [];
  let start = 0;
  for (const [order, nucleus] of nuclei.entries()) {
    const next = nuclei[order + 1];
    let end = phones.length;
    if (next !== undefined) {
      const cluster = phones.slice(nucleus + 1, next);
      end = nucleus + 1 + onsetStart(cluster);
    }
    syllables.push({
      phones: phones.slice(start, end),
      stress: phones[nucleus].stress,
    });
    start = end;
  }
  return syllables;
}

export interface IpaOptions {
  /** Write ˈ and ˌ before stressed syllables (never on a one-syllable word). */
  stressMarks: boolean;
}

/** ARPAbet → IPA: `"F L AW1 ER0"` → `ˈflaʊɚ`. Null if the string is not ARPAbet. */
export function arpabetToIpa(
  pron: string,
  options: IpaOptions = { stressMarks: true }
): string | null {
  const phones = parseArpabet(pron);
  if (!phones) {
    return null;
  }
  const syllables = syllabify(phones);
  const marked = options.stressMarks && syllables.length > 1;
  return syllables
    .map((syllable) => {
      let mark = "";
      if (marked && syllable.stress === 1) {
        mark = "ˈ";
      } else if (marked && syllable.stress === 2) {
        mark = "ˌ";
      }
      return mark + syllable.phones.map(phoneToIpa).join("");
    })
    .join("");
}

/* --------------------------------------------------------------- the text */

export type TextToken =
  | { kind: "word"; text: string; key: string }
  | { kind: "other"; text: string };

/** A word: letters, with apostrophes inside it (don't, o'clock). */
const RE_WORD = /\p{L}+(?:['’]\p{L}+)*/gu;
const RE_CURLY_APOSTROPHE = /’/g;
const RE_DIGIT = /\d/;

/**
 * Split text into words and everything else, keeping every character, so the
 * transcription can be rebuilt with the original spaces, line breaks and punctuation.
 * A hyphenated compound is two words — "well-known" is looked up as "well" and "known".
 */
export function tokenizeText(text: string): TextToken[] {
  const tokens: TextToken[] = [];
  let last = 0;
  for (const match of text.matchAll(RE_WORD)) {
    const index = match.index ?? 0;
    if (index > last) {
      tokens.push({ kind: "other", text: text.slice(last, index) });
    }
    tokens.push({
      kind: "word",
      text: match[0],
      key: match[0].toLowerCase().replace(RE_CURLY_APOSTROPHE, "'"),
    });
    last = index + match[0].length;
  }
  if (last < text.length) {
    tokens.push({ kind: "other", text: text.slice(last) });
  }
  return tokens;
}

/** The distinct words of a text, in order of first appearance. */
export function uniqueWords(tokens: readonly TextToken[]): string[] {
  const seen = new Set<string>();
  for (const token of tokens) {
    if (token.kind === "word") {
      seen.add(token.key);
    }
  }
  return [...seen];
}

export function hasDigits(text: string): boolean {
  return RE_DIGIT.test(text);
}

/* ------------------------------------------------------------- the lookup */

/** One word's question: the query echo carries its pronunciation and frequency. */
export function pronunciationQuery(word: string): string {
  return datamuseQuery({ sp: word, qe: "sp", md: "rf", ipa: 1, max: 1 });
}

export interface Pronunciation {
  word: string;
  /** ARPAbet from the CMU dictionary, or Datamuse's guess for an unknown word. */
  arpabet: string | null;
  /** Datamuse's own IPA, the fallback when the ARPAbet cannot be parsed. */
  datamuseIpa: string | null;
  /**
   * False when the word is not in the CMU dictionary, so its pronunciation is a guess
   * from the spelling. When the bundled dictionary could not be loaded, Datamuse's
   * frequency stands in: zero means Datamuse has never seen the word.
   */
  known: boolean;
}

/** A word the bundled dictionary has: its first CMU pronunciation. */
export function dictionaryPronunciation(
  word: string,
  arpabet: string
): Pronunciation {
  return { word, arpabet, datamuseIpa: null, known: true };
}

/**
 * A word the bundled dictionary lacks, as Datamuse answers it. Wherever the CMU
 * dictionary has a word, Datamuse's pronunciation is the dictionary's (less its
 * secondary stress), and its documentation says it guesses the rest from the spelling —
 * so a word outside the dictionary is a guess, however often Datamuse has seen it.
 */
export function guessedPronunciation(
  word: string,
  results: readonly WordResult[]
): Pronunciation {
  return { ...pronunciationOf(word, results), known: false };
}

/** A word as Datamuse answers it, when there is no dictionary to check it against. */
export function pronunciationOf(
  word: string,
  results: readonly WordResult[]
): Pronunciation {
  const echo = splitEcho(results).echo;
  return {
    word,
    arpabet: echo?.arpabet ?? null,
    datamuseIpa: echo?.ipa ?? null,
    known: (echo?.frequency ?? 0) > 0,
  };
}

/** The IPA for one looked-up word, or null when there is nothing usable. */
export function ipaFor(
  pronunciation: Pronunciation,
  options: IpaOptions
): string | null {
  const converted = pronunciation.arpabet
    ? arpabetToIpa(pronunciation.arpabet, options)
    : null;
  return converted ?? pronunciation.datamuseIpa;
}

/**
 * Rebuild the text with each word replaced by its IPA. A word with no pronunciation is
 * left as written, so nothing silently disappears from the transcription.
 */
export function transcribe(
  tokens: readonly TextToken[],
  ipaOf: (key: string) => string | null
): string {
  return tokens
    .map((token) =>
      token.kind === "word" ? (ipaOf(token.key) ?? token.text) : token.text
    )
    .join("");
}

/** Put each non-empty line between slashes, phonemic style: /ðə ˈkwaɪət kæt/. */
export function wrapLines(text: string): string {
  return text
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();
      return trimmed ? `/${trimmed}/` : line;
    })
    .join("\n");
}
